import {
  ComponentStyleConfig,
  SystemStyleInterpolation,
} from '@chakra-ui/react';

import {
  glassyEffectButton,
  glassyEffectButtonHover,
} from '@/styles/variants/glassy';

const socialAuthVariant: SystemStyleInterpolation = {
  colorScheme: 'white',
  color: 'buttons.social-auth.font',
  bg: 'buttons.social-auth.bg',
  borderColor: 'buttons.social-auth.border!',
  border: '1px solid',
  _hover: {
    bg: 'buttons.social-auth.bg-hover',
    '& .chakra-button__icon *': {
      fill: 'buttons.social-auth.icon-hover',
    },
  },
  _disabled: {
    bg: 'buttons.social-auth.bg',
    color: 'buttons.social-auth.font-disabled',
    '& .chakra-button__icon *': {
      fill: 'buttons.social-auth.icon-disabled',
    },
  },
  _loading: {
    color: 'buttons.social-auth.font',
    '& .chakra-spinner': {
      color: 'spinner.main',
      emptyColor: 'gray.200',
    },
  },
};

const glassItemVariant: SystemStyleInterpolation = {
  colorScheme: 'gray',
  bg: 'glass-items.bg',
  border: '1px solid',
  borderColor: 'glass-items.border',
  color: 'glass-items.icon',
  _active: {
    bg: 'glass-items.bg-active',
  },
  _hover: {
    bg: 'glass-items.bg-hover',
    borderColor: 'glass-items.border-hover',
    color: 'glass-items.icon-hover',
  },
};

const primaryVariant: SystemStyleInterpolation = {
  colorScheme: 'purple',
  color: 'buttons.primary.font',
  bg: 'buttons.primary.bg',
  borderColor: 'buttons.primary.bg',
  _hover: {
    bg: 'buttons.primary.bg-hover',
  },
  _disabled: {
    bg: 'buttons.primary.bg-disabled',
    borderColor: 'buttons.primary.bg-disabled',
  },
};

export const secondaryVariant: SystemStyleInterpolation = {
  colorScheme: 'white',
  border: '1px solid',
  borderColor: 'white.100',
  borderRadius: 'xl',
  bg: 'search-input.bg-filled',
  backdropFilter: 'blur(12px)',
  color: 'buttons.social-auth.font!',
  _hover: {
    borderColor: 'white.100',
    bg: 'buttons.social-auth.bg-hover',
  },
  _active: {
    borderColor: 'white.100',
    bg: 'buttons.social-auth.bg-hover!',
  },
  _disabled: {
    opacity: 1,
    borderColor: 'white.100',
    bg: 'buttons.social-auth.bg-disabled',
    color: 'buttons.social-auth.font-disabled!',
    '& .chakra-button__icon *': {
      fill: 'buttons.social-auth.font-disabled!',
    },
  },
  _loading: {
    color: 'buttons.social-auth.font!',
    '& .chakra-spinner': {
      color: 'spinner.main!',
      emptyColor: 'gray.200',
    },
  },
};

const outlineVariant: SystemStyleInterpolation = {
  _disabled: {
    opacity: 0.5,
  },
};

const textButtonHover: SystemStyleInterpolation = {
  opacity: 0.8,
  textDecoration: 'underline',
  bg: 'transparent',
};

const textButtonVariant: SystemStyleInterpolation = {
  fontWeight: '400',
  minW: 'auto',
  minH: 'auto',
  h: 'auto',
  px: 0,
  py: 0,
  _hover: textButtonHover,
  _active: {
    bg: 'transparent',
  },
};

const underlineVariant: SystemStyleInterpolation = {
  ...textButtonVariant,
  color: 'buttons.underline.font',
  textDecoration: 'underline',
  _hover: {
    ...textButtonHover,
    color: 'buttons.underline.font-hover',
  },
};

const linkVariant: SystemStyleInterpolation = {
  ...textButtonVariant,
  color: 'secondary.500',
};

export const glassyVariant: SystemStyleInterpolation = {
  ...glassyEffectButton,
  ...glassyEffectButtonHover,
};

export const buttonTheme: ComponentStyleConfig = {
  baseStyle: {
    fontSize: 'sm',
    lineHeight: '5',
    transition: 'all 0.3s ease-out',
    _disabled: {
      pointerEvents: 'none',
      opacity: '0.85 !important',
    },
  },
  sizes: {
    xs: {
      minW: '6',
      minH: '6',
      h: '6',
      rounded: '8px',
      fontSize: 'sm',
    },
    sm: {
      minW: '8',
      minH: '8',
      h: '8',
      rounded: '8px',
      fontSize: 'sm',
    },
    md: {
      minW: '8',
      minH: '8',
      h: '8',
      rounded: '8px',
      fontSize: '16px',
    },
    lg: {
      minW: '8',
      minH: '10',
      h: '10',
      rounded: 'xl',
      fontSize: '16px',
    },
    xl: {
      minW: '8',
      minH: '10',
      px: '4',
      h: '10',
      rounded: 'xl',
      fontSize: '16px',
    },
  },
  variants: {
    'social-auth': socialAuthVariant,
    primary: primaryVariant,
    secondary: secondaryVariant,
    'glass-item': glassItemVariant,
    glassy: glassyVariant,
    link: linkVariant,
    underline: underlineVariant,
    outline: outlineVariant,
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
};
